import {
  type ComponentType,
  createElement,
  isValidElement,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import { Link, type To } from "react-router";
import { CircularProgress, type TooltipProps } from "@mui/material";
import cx from "classnames";
import TruncationAwareTooltip from "src/components/general/TruncationAwareTooltip/TruncationAwareTooltip";
import WrapIf from "src/components/utils/WrapIf/WrapIf";

export interface BreadcrumbLinkProps {
  children: ReactNode;
  className?: string;
  isLoading?: boolean;
  to?: To;
  startAdornment?: ComponentType<any> | ReactElement<any>;
  endAdornment?: ComponentType<any> | ReactElement<any>;
  tooltip?: ReactNode;
  TooltipComponent?: ComponentType<any>;
  "data-test-id"?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement | HTMLDivElement>) => void;
  onMouseDownCapture?: (e: MouseEvent<HTMLAnchorElement | HTMLDivElement>) => void;
  ref?: Ref<HTMLAnchorElement | HTMLDivElement>;
  slotProps?: {
    tooltip?: Partial<TooltipProps> & { force?: boolean };
  };
}

function BreadcrumbLink({
  children,
  className,
  isLoading,
  startAdornment,
  endAdornment,
  tooltip,
  TooltipComponent = TruncationAwareTooltip,
  slotProps,
  to,
  ref,
  ...props
}: BreadcrumbLinkProps) {
  if (isLoading) {
    return <CircularProgress size="12px" />;
  }

  const isTruncationAwareTooltip = TooltipComponent === TruncationAwareTooltip;
  const rootClassName = cx(
    "no-underline text-text-secondary flex items-center whitespace-nowrap",
    className
  );

  const inner = (
    <TooltipComponent arrow title={tooltip} {...slotProps?.tooltip}>
      <WrapIf
        condition={!isTruncationAwareTooltip}
        With="div"
        className="inline-flex items-center w-full"
      >
        {startAdornment &&
          (isValidElement(startAdornment)
            ? startAdornment
            : createElement(startAdornment as ComponentType<any>, {
                fontSize: "small",
                className: "mr-1 align-middle",
              }))}

        {children}
        {endAdornment &&
          (isValidElement(endAdornment)
            ? endAdornment
            : createElement(endAdornment as ComponentType<any>, {
                fontSize: "small",
                className: "mr-1",
              }))}
      </WrapIf>
    </TooltipComponent>
  );

  const sharedProps = {
    className: rootClassName,
    ...props,
    "data-test-id": props["data-test-id"] || children,
  };

  if (to) {
    return (
      <Link ref={ref as React.Ref<HTMLAnchorElement>} to={to} {...sharedProps}>
        {inner}
      </Link>
    );
  }

  return (
    <div ref={ref as React.Ref<HTMLDivElement>} {...sharedProps}>
      {inner}
    </div>
  );
}

export default BreadcrumbLink;
